// xlsx template I/O — uses ExcelJS as an npm dependency.
import ExcelJS from 'exceljs';
import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { CATEGORIES, CAT_BY_ID, CAT_TREE } from '../../data/categories.js';
import { SLOTS, HALF_HOUR_HOURS, emptyDay, dateKey } from '../../data/slots.js';
import { colorNameForHex } from '../../data/palettes.js';

// Built-in palette (RGB hex without #). Custom categories are merged in at runtime by reading
// CAT_BY_ID[catId].color — so adding 赖床 / 卧床 / etc. picks up their fill automatically.
const TEMPLATE_PALETTE = {
  sleep:    'B49EE0',
  prep_xnx: '7AA8D8',
  prep_pr:  'C5E2F0',
  prep:     '6090CC',
  exercise: 'E8588C',
  focus:    'B6D880',
  eat:      'F7D560',
  fun:      'F2A04D',
};

function normalizeHex(s) {
  if (!s) return null;
  s = String(s).trim();
  if (!s.startsWith('#')) return null;
  s = s.slice(1).toUpperCase();
  if (s.length === 3) s = s.split('').map(c => c + c).join('');
  return /^[0-9A-F]{6}$/.test(s) ? s : null;
}

// 解析颜色到 6 位 hex：var(--x) 走 getComputedStyle（导出在浏览器里跑，CSS 变量可解析），否则直接 normalize。
// 让子分类（瑜伽/求职/拉…用 var(--c-xxx)）的真实色也能进调色板 + 导入按色识别。
function resolveColorHex(color) {
  if (!color) return null;
  let s = String(color).trim();
  const m = s.match(/^var\((--[A-Za-z0-9-]+)\)$/);
  if (m) {
    if (typeof document === 'undefined') return null;
    s = getComputedStyle(document.documentElement).getPropertyValue(m[1]).trim();
  }
  return normalizeHex(s);
}

function getCatColor(catId) {
  if (!catId) return null;
  if (TEMPLATE_PALETTE[catId]) return TEMPLATE_PALETTE[catId];
  const c = CAT_BY_ID && CAT_BY_ID[catId];
  if (c && c.color) return resolveColorHex(c.color);
  return null;
}

function colorToCat(rgb) {
  if (!rgb) return null;
  rgb = rgb.toUpperCase();
  for (const [k, v] of Object.entries(TEMPLATE_PALETTE)) {
    if (v.toUpperCase() === rgb) return k;
  }
  for (const c of (CATEGORIES || [])) {
    const h = resolveColorHex(c.color);
    if (h === rgb) return c.id;
  }
  return null;
}

function buildTemplateSlots() {
  // 复用 slots.js 的 half-hour 集合，避免与主表切分漂移（N36 加了 6 点半点格）
  const HALF = HALF_HOUR_HOURS;
  const out = [];
  for (let h = 0; h < 24; h++) {
    if (HALF.has(h)) {
      out.push({ h, m: 0, span: 0.5 });
      out.push({ h, m: 30, span: 0.5 });
    } else {
      out.push({ h, m: 0, span: 1 });
    }
  }
  return out;
}
const TEMPLATE_SLOTS = buildTemplateSlots();
// 模版时间表头：整点格只显数字（1/2/…/23，简洁），半点格显「h:30」区分。导入按列位置映射、不读此标签文字。
function slotLabel(s) { return s.m === 30 ? `${s.h}:30` : String(s.h); }
function fillFor(rgb) { return { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + rgb } }; }

// ---- Export ----
const META_ROWS = [
  ['三餐·早',        'meals.breakfast', 'meal',   { list: ['按时', '拖延'] }],
  ['三餐·午',        'meals.lunch',     'meal',   { list: ['按时', '拖延'] }],
  ['三餐·晚',        'meals.dinner',    'meal',   { list: ['按时', '拖延'] }],
  ['睡眠·时长档',    'sleepDuration',   'str',    { list: ['3-6h', '6-9h', '9-12h', '12h+'] }],
  ['睡眠·夜间 h',    'sleepHours',      'num',    {}],
  ['睡眠·零星 h',    'napHours',        'num',    {}],
  ['睡眠·半夜醒',    'nightWake',       'num',    {}],
  ['睡眠·睡前刷手机','phoneBeforeSleep','bool',   { list: ['是', '否'] }],
  ['睡眠·睡前听书',  'listenBeforeSleep','bool',  { list: ['是', '否'] }],
  ['身体·骨量 kg',   'boneMass',        'num',    { blankDefault: 1.8 }],
  ['身体·体重 kg',   'weight',          'num',    { blankDefault: 46.5 }],
  ['情绪·平稳',      'emo.平稳',        'bool',   { list: ['是', '否'], multiSrc: 'emotions' }],
  ['情绪·焦虑',      'emo.焦虑',        'bool',   { list: ['是', '否'], multiSrc: 'emotions' }],
  ['情绪·内耗',      'emo.内耗',        'bool',   { list: ['是', '否'], multiSrc: 'emotions' }],
  ['情绪·压抑',      'emo.压抑',        'bool',   { list: ['是', '否'], multiSrc: 'emotions' }],
  ['情绪·委屈',      'emo.委屈',        'bool',   { list: ['是', '否'], multiSrc: 'emotions' }],
  ['情绪·心累',      'emo.心累',        'bool',   { list: ['是', '否'], multiSrc: 'emotions' }],
  ['效率',           'efficiency',      'str',    { list: ['低能量', '一般', '高效'] }],
  ['运动·公园',      'ex.公园',         'bool',   { list: ['是', '否'], multiSrc: 'exerciseTypes' }],
  ['运动·羽毛球',    'ex.羽毛球',       'bool',   { list: ['是', '否'], multiSrc: 'exerciseTypes' }],
  ['运动·瑜伽',      'ex.瑜伽',         'bool',   { list: ['是', '否'], multiSrc: 'exerciseTypes' }],
  ['运动·无',        'ex.无',           'bool',   { list: ['是', '否'], multiSrc: 'exerciseTypes' }],
  ['主要事件',       'mainEvent',       'str',    {}],
];

function getDeep(obj, path) {
  return path.split('.').reduce((o, k) => (o == null ? null : o[k]), obj);
}
function setDeep(obj, path, val) {
  const parts = path.split('.');
  let o = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (o[parts[i]] == null) o[parts[i]] = {};
    o = o[parts[i]];
  }
  o[parts[parts.length - 1]] = val;
}

function metaValToCell(day, key, kind, opt) {
  if (opt && opt.multiSrc) {
    const optionName = key.split('.')[1];
    const arr = day[opt.multiSrc] || [];
    return arr.includes(optionName) ? '是' : '否';
  }
  const v = getDeep(day, key);
  if (v == null || v === '') return '';
  if (kind === 'meal') return v === 'ontime' ? '按时' : v === 'late' ? '拖延' : '';
  if (kind === 'bool') return v ? '是' : '否';
  return v;
}

function cellValToMeta(raw, kind) {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (s === '') return null;
  if (kind === 'meal') return s === '按时' ? 'ontime' : s === '拖延' ? 'late' : null;
  if (kind === 'bool') return /^(是|true|1|yes)$/i.test(s);
  if (kind === 'arr') return s.split(/[,，]/).map(x => x.trim()).filter(Boolean);
  if (kind === 'num') { const n = parseFloat(s); return isNaN(n) ? null : n; }
  return s;
}

async function writeDaySheet(wb, dk, day, options = {}) {
  const ws = wb.addWorksheet(dk);
  ws.columns = [{ width: 16 }, ...TEMPLATE_SLOTS.map(() => ({ width: 5 }))];

  function colRangeForHours(hFrom, hTo) {
    let firstCol = null, lastCol = null;
    TEMPLATE_SLOTS.forEach((sl, i) => {
      if (sl.h >= hFrom && sl.h <= hTo) {
        const col = i + 2;
        if (firstCol == null) firstCol = col;
        lastCol = col;
      }
    });
    return [firstCol, lastCol];
  }
  function colLetter(col) {
    let s = '';
    while (col > 0) { const r = (col - 1) % 26; s = String.fromCharCode(65 + r) + s; col = Math.floor((col - 1) / 26); }
    return s;
  }

  // ---- Row 1-2: Palette banner ----
  // 所有固化时间分类（能解析出颜色的）单排一遍，不再左右两头各画一遍（分类多了会对撞排不下）。
  // 顺序按 CAT_TREE 树结构：父 → 它的子（内置 children + parentId 挂载的种子/自定义子）→ 下一个父。
  // 例：睡眠 → 赖床 → 躺着没睡 → 预备 → 小能熊 → 怦然 → …
  const childMap = {};
  CAT_TREE.forEach(n => { if (n.children) childMap[n.id] = [...n.children]; });
  (CATEGORIES || []).forEach(c => { if (c.parentId) (childMap[c.parentId] = childMap[c.parentId] || []).push(c.id); });
  const orderedIds = [];
  CAT_TREE.forEach(n => {
    orderedIds.push(n.id);
    (childMap[n.id] || []).forEach(cid => { if (!orderedIds.includes(cid)) orderedIds.push(cid); });
  });
  (CATEGORIES || []).forEach(c => { if (!orderedIds.includes(c.id)) orderedIds.push(c.id); }); // 兜底：树外的也带上
  const PALETTE_ORDER = orderedIds
    .map(id => { const c = CAT_BY_ID[id]; return c ? { id, name: c.name || c.label || id } : null; })
    .filter(p => p && getCatColor(p.id));
  ws.getRow(1).getCell(1).value = '调色板 →';
  ws.getRow(1).getCell(1).font = { bold: true, size: 10, color: { argb: 'FF888888' } };
  ws.getRow(1).getCell(1).alignment = { horizontal: 'right', vertical: 'middle' };
  ws.getRow(1).height = 22;
  ws.getRow(2).height = 16;
  PALETTE_ORDER.forEach((p, i) => {
    const col = 2 + i;
    const swatch = ws.getRow(1).getCell(col);
    swatch.fill = fillFor(getCatColor(p.id) || 'CCCCCC');
    const label = ws.getRow(2).getCell(col);
    label.value = p.name;
    label.font = { size: 9, color: { argb: 'FF666666' } };
    label.alignment = { horizontal: 'center' };
  });
  // 提示放在调色板之后（左对齐溢出到右侧空列；不再居中以免与变长的调色板重叠）
  const hintCell = ws.getRow(1).getCell(2 + PALETTE_ORDER.length + 1);
  hintCell.value = '← 左边色块任选一格，复制粘贴到下方时间块；或用格式刷批量上色';
  hintCell.font = { italic: true, size: 9, color: { argb: 'FFAAAAAA' } };
  hintCell.alignment = { horizontal: 'left' };

  // ---- Row 3: Time segments ----
  const TIME_SEGMENTS = [
    { label: '凌晨', range: [0, 5]  },
    { label: '早晨', range: [6, 9]  },
    { label: '上午', range: [10, 12] },
    { label: '中午', range: [13, 15] },
    { label: '下午', range: [16, 18] },
    { label: '晚上', range: [19, 21] },
    { label: '夜间', range: [22, 23] },
  ];
  ws.getRow(3).height = 20;
  ws.getRow(3).getCell(1).value = '时段';
  ws.getRow(3).getCell(1).font = { size: 10, color: { argb: 'FF888888' } };
  ws.getRow(3).getCell(1).alignment = { horizontal: 'right' };
  TIME_SEGMENTS.forEach(seg => {
    const [a, b] = colRangeForHours(seg.range[0], seg.range[1]);
    if (a == null) return;
    if (a !== b) ws.mergeCells(`${colLetter(a)}3:${colLetter(b)}3`);
    const cell = ws.getRow(3).getCell(a);
    cell.value = seg.label;
    cell.font = { bold: true, size: 11, color: { argb: 'FF555555' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
      right: { style: 'thin', color: { argb: 'FFE0E0E0' } },
      bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
    };
  });

  // ---- Row 4: Recommended schedule (colored bands) ----
  const RECOMMEND_BANDS = [
    { from: 0,  to: 5,  label: '预计睡眠', color: 'DCC9F0' },
    { from: 6,  to: 6,  label: '起床',     color: 'C8E6C9' },
    { from: 7,  to: 8,  label: '早餐运动', color: 'C8E6C9' },
    { from: 9,  to: 11, label: '高效记忆', color: 'C8E6C9' },
    { from: 12, to: 12, label: '午餐',     color: 'C8E6C9' },
    { from: 13, to: 14, label: '午睡',     color: 'EBDDF7' },
    { from: 15, to: 17, label: '计算',     color: 'C8E6C9' },
    { from: 18, to: 19, label: '晚餐',     color: 'C8E6C9' },
    { from: 20, to: 21, label: '瑜伽',     color: 'C8E6C9' },
    { from: 22, to: 22, label: '准备睡觉', color: 'C8E6C9' },
    { from: 23, to: 23, label: '睡觉',     color: 'DCC9F0' },
  ];
  ws.getRow(4).height = 20;
  ws.getRow(4).getCell(1).value = '推荐';
  ws.getRow(4).getCell(1).font = { size: 10, color: { argb: 'FF888888' } };
  ws.getRow(4).getCell(1).alignment = { horizontal: 'right' };
  RECOMMEND_BANDS.forEach(b => {
    const [a, e] = colRangeForHours(b.from, b.to);
    if (a == null) return;
    if (a !== e) ws.mergeCells(`${colLetter(a)}4:${colLetter(e)}4`);
    const cell = ws.getRow(4).getCell(a);
    cell.value = b.label;
    cell.fill = fillFor(b.color);
    cell.font = { size: 10, color: { argb: 'FF555555' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
      right: { style: 'thin', color: { argb: 'FFE0E0E0' } },
      bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
    };
  });

  // ---- Row 5: date + time HH:MM headers ----
  const r5 = ws.getRow(5);
  r5.height = 20;
  r5.getCell(1).value = dk;
  r5.getCell(1).font = { bold: true, size: 12 };
  TEMPLATE_SLOTS.forEach((sl, i) => {
    const c = r5.getCell(i + 2);
    c.value = slotLabel(sl);
    c.font = { size: 9, color: { argb: 'FF888888' } };
    c.alignment = { horizontal: 'center' };
  });

  // ---- Row 6-11: tracks interleaved with notes ----
  const tracks = day.tracks || [[], [], []];
  const notes = day.notes || {};
  const TRACK_NAMES = ['娱乐·吃', '睡眠', '运动'];
  const NOTE_NAMES = ['备注·娱乐·吃', '备注·睡眠', '备注·运动'];
  for (let t = 0; t < 3; t++) {
    const trackRow = ws.getRow(6 + t * 2);
    trackRow.height = 24;
    trackRow.getCell(1).value = TRACK_NAMES[t];
    trackRow.getCell(1).font = { bold: true };
    trackRow.getCell(1).alignment = { horizontal: 'right', vertical: 'middle' };
    TEMPLATE_SLOTS.forEach((sl, i) => {
      const realIdx = SLOTS.findIndex(x => x.h === sl.h && x.m === sl.m);
      const catId = realIdx >= 0 ? tracks[t] && tracks[t][realIdx] : null;
      const rgb = catId ? getCatColor(catId) : null;
      if (rgb) trackRow.getCell(i + 2).fill = fillFor(rgb);
      trackRow.getCell(i + 2).border = {
        top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
        bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
        left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
        right: { style: 'thin', color: { argb: 'FFE0E0E0' } },
      };
    });
    const noteRow = ws.getRow(7 + t * 2);
    noteRow.height = 18;
    noteRow.getCell(1).value = NOTE_NAMES[t];
    noteRow.getCell(1).font = { italic: true, size: 10, color: { argb: 'FF888888' } };
    noteRow.getCell(1).alignment = { horizontal: 'right' };
    let runStartIdx = 0, runCat = null, runNote = null;
    function emit() {
      if (runCat != null && runNote) {
        const startSlot = SLOTS[runStartIdx];
        const colIdx = TEMPLATE_SLOTS.findIndex(x => x.h === startSlot.h && x.m === startSlot.m);
        if (colIdx >= 0) noteRow.getCell(colIdx + 2).value = runNote;
      }
    }
    const trackArr = tracks[t] || [];
    for (let i = 0; i < trackArr.length; i++) {
      const cat = trackArr[i];
      if (cat !== runCat) { emit(); runStartIdx = i; runCat = cat; runNote = null; }
      if (cat != null) {
        const note = notes[`${t}:${i}`];
        if (note && !runNote) runNote = note;
      }
    }
    emit();
  }

  // ---- Row 12 label, Row 13+ metadata ----
  const META_START_ROW = 13;
  ws.getRow(META_START_ROW - 1).getCell(1).value = '— 当日元数据 —';
  ws.getRow(META_START_ROW - 1).getCell(1).font = { bold: true, size: 11, color: { argb: 'FF888888' } };
  META_ROWS.forEach((m, i) => {
    const [label, key, kind, opt = {}] = m;
    const row = ws.getRow(META_START_ROW + i);
    row.getCell(1).value = label;
    row.getCell(1).font = { color: { argb: 'FF666666' } };
    row.getCell(1).alignment = { horizontal: 'right' };
    let val = metaValToCell(day, key, kind, opt);
    if ((val === '' || val == null) && opt.blankDefault != null && options.blank) {
      val = opt.blankDefault;
    }
    if (val !== '' && val != null) row.getCell(2).value = val;
    row.getCell(2).font = { bold: true };
    if (opt.list && opt.list.length) {
      row.getCell(2).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`"${opt.list.join(',')}"`],
      };
    }
    let hint = '';
    if (opt.list) hint = opt.list.join(' / ');
    if (kind === 'num' && !opt.list) hint = '数值';
    if (hint) {
      row.getCell(3).value = hint;
      row.getCell(3).font = { italic: true, size: 9, color: { argb: 'FFAAAAAA' } };
    }
  });
  ws.views = [{ state: 'frozen', xSplit: 1, ySplit: 5 }];
}

// 由 6 位 hex 估一个中文颜色描述词（自包含，不依赖 app 调色板命名表）。
function colorNameZh(hex) {
  if (!hex || !/^[0-9A-Fa-f]{6}$/.test(hex)) return '';
  const r = parseInt(hex.slice(0, 2), 16) / 255, g = parseInt(hex.slice(2, 4), 16) / 255, b = parseInt(hex.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min, l = (max + min) / 2;
  if (d < 0.06) return l > 0.82 ? '近白' : l < 0.18 ? '近黑' : '灰';
  let h = max === r ? ((g - b) / d + (g < b ? 6 : 0)) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  h *= 60;
  const base = h < 15 || h >= 345 ? '红' : h < 40 ? '橙' : h < 65 ? '黄' : h < 160 ? '绿' : h < 200 ? '青' : h < 255 ? '蓝' : h < 290 ? '紫' : '粉';
  const prefix = l > 0.74 ? '浅' : l < 0.4 ? '深' : '';
  return prefix + base;
}

// 图例 sheet：一行一个时间分类，含 色块/色码/颜色描述/树形(父分类+缩进)/id/快捷键。排在日期 sheet 之后。
function writeLegendSheet(wb) {
  const ws = wb.addWorksheet('图例');
  const childMap = {}, parentOf = {};
  CAT_TREE.forEach(n => { if (n.children) { childMap[n.id] = [...n.children]; n.children.forEach(cid => { parentOf[cid] = n.id; }); } });
  (CATEGORIES || []).forEach(c => { if (c.parentId) { (childMap[c.parentId] = childMap[c.parentId] || []).push(c.id); parentOf[c.id] = c.parentId; } });
  const ordered = [];
  CAT_TREE.forEach(n => { ordered.push(n.id); (childMap[n.id] || []).forEach(cid => { if (!ordered.includes(cid)) ordered.push(cid); }); });
  (CATEGORIES || []).forEach(c => { if (!ordered.includes(c.id)) ordered.push(c.id); });

  ws.columns = [
    { header: '时间分类', key: 'name', width: 16 },
    { header: '父分类', key: 'parent', width: 12 },
    { header: '色块', key: 'sw', width: 8 },
    { header: '色码', key: 'hex', width: 11 },
    { header: '颜色描述', key: 'desc', width: 10 },
    { header: 'id', key: 'id', width: 14 },
    { header: '快捷键', key: 'key', width: 8 },
  ];
  ws.getRow(1).font = { bold: true };
  ws.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0ECF6' } };
  ws.views = [{ state: 'frozen', ySplit: 1 }];

  ordered.forEach(id => {
    const c = CAT_BY_ID[id];
    if (!c) return;
    const hex = getCatColor(id);
    const pid = parentOf[id];
    const pname = pid && CAT_BY_ID[pid] ? (CAT_BY_ID[pid].name || CAT_BY_ID[pid].label || pid) : '';
    const row = ws.addRow({
      name: (pid ? '└ ' : '') + (c.name || c.label || id),
      parent: pname,
      sw: '',
      hex: hex ? '#' + hex : '',
      desc: colorNameForHex(hex) || colorNameZh(hex), // 系统色名优先，无匹配回退自动估色
      id,
      key: c.key || '',
    });
    if (hex) row.getCell(3).fill = fillFor(hex);
    row.getCell(7).alignment = { horizontal: 'center' };
  });
}

async function exportInternal(data, dates, filename, options = {}) {
  try {
    const wb = new ExcelJS.Workbook();
    wb.creator = '时间块系统';
    if (dates.length === 0) { alert('没有数据可导出'); return; }
    for (const dk of dates) {
      const day = data[dk] || emptyDay(dk);
      await writeDaySheet(wb, dk, day, options);
    }
    writeLegendSheet(wb); // 图例 sheet 排在所有日期 sheet 之后
    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 200);
  } catch (e) {
    console.error(e);
    alert('导出失败：' + e.message);
  }
}

export async function exportTemplate(data) {
  const dates = Object.keys(data || {}).sort();
  return exportInternal(data, dates, `时间块·可视_${new Date().toISOString().slice(0, 10)}.xlsx`);
}

export async function exportTemplateForDate(data, dk) {
  if (!dk) { alert('未指定日期'); return; }
  return exportInternal(data, [dk], `时间块·${dk}.xlsx`);
}

export async function exportBlankTemplate() {
  const today = new Date();
  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(today); d.setDate(today.getDate() + i);
    days.push(dateKey(d));
  }
  const blank = {};
  days.forEach(k => { blank[k] = emptyDay(k); });
  return exportInternal(blank, days, `时间块模板_空白.xlsx`, { blank: true });
}

// ---- 数据导出：单个 xlsx，3 个 sheet（每日元数据 / 时间块 / 行经记录），扁平平表便于查阅分析 ----
// 区别于上面的"可视模板"（每天一 sheet 的彩色网格、用于双向同步）。
const DATA_TRACK_NAMES = ['娱乐·吃', '睡眠', '运动'];
function mealLabel(v) { return v === 'ontime' ? '按时' : v === 'late' ? '拖延' : ''; } // 三餐 null 维持空（B 口径，不动 N37）

// 合并相邻同分类格为块（与 csv.js 同算法；含半小时格 end 计算）
function dayBlocks(day) {
  const out = [];
  if (!day || !day.tracks) return out;
  const pad = (n) => String(n).padStart(2, '0');
  for (let t = 0; t < 3; t++) {
    const arr = day.tracks[t] || [];
    let runStart = null, runCat = null, runNote = null;
    const emit = (endIdx) => {
      if (runCat == null || runStart == null) return;
      const s = SLOTS[runStart], e = SLOTS[endIdx];
      const endM = e.m + (e.span === 0.5 ? 30 : 60);
      const eh = endM >= 60 ? e.h + 1 : e.h;
      const em = endM >= 60 ? endM - 60 : endM;
      out.push({ track: t, start: `${pad(s.h)}:${pad(s.m)}`, end: `${pad(eh)}:${pad(em)}`, catId: runCat, note: runNote || '' });
    };
    for (let i = 0; i < arr.length; i++) {
      const cat = arr[i];
      const note = (day.notes || {})[`${t}:${i}`];
      if (cat !== runCat) { emit(i - 1); runStart = i; runCat = cat; runNote = note || null; }
      else if (cat != null && note && !runNote) runNote = note;
    }
    emit(arr.length - 1);
  }
  return out;
}

// 行经数据在 day.cycle 对象里（live CycleEditor；扁平 bloodFlow/cramps 是旧死代码，不读）
const CYCLE_PHASE_LABEL = { menses: '行经', ovulation: '排卵期' };
const CYCLE_FLOW_LABEL = { 1: '少', 2: '一般', 3: '多', 4: '很多' };
const CYCLE_PAIN_LABEL = ['无', '轻', '中', '重', '严重', '剧烈'];

function styleDataHeader(ws) {
  const row = ws.getRow(1);
  row.font = { bold: true };
  row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0ECF6' } };
  ws.views = [{ state: 'frozen', ySplit: 1 }];
}

export async function exportData3Sheet(data) {
  try {
    const dates = Object.keys(data || {}).sort();
    if (dates.length === 0) { alert('没有数据可导出'); return; }
    const wb = new ExcelJS.Workbook();
    wb.creator = '时间块系统';

    // Sheet 1 · 每日元数据（一天一行）
    const meta = wb.addWorksheet('每日元数据');
    meta.columns = [
      { header: '日期', key: 'date', width: 12 }, { header: '三餐-早', key: 'b', width: 8 },
      { header: '三餐-午', key: 'l', width: 8 }, { header: '三餐-晚', key: 'd', width: 8 },
      { header: '睡眠档', key: 'sd', width: 8 }, { header: '夜间h', key: 'sh', width: 7 },
      { header: '零星h', key: 'nh', width: 7 }, { header: '半夜醒', key: 'nw', width: 7 },
      { header: '睡前刷手机', key: 'ph', width: 11 }, { header: '睡前听书', key: 'li', width: 10 },
      { header: '骨量kg', key: 'bm', width: 8 }, { header: '体重kg', key: 'wt', width: 8 },
      { header: '效率', key: 'ef', width: 8 }, { header: '情绪', key: 'em', width: 16 },
      { header: '运动类型', key: 'ex', width: 14 }, { header: '主要事件', key: 'me', width: 24 },
    ];
    dates.forEach(k => {
      const d = data[k];
      meta.addRow({
        date: k, b: mealLabel(d.meals?.breakfast), l: mealLabel(d.meals?.lunch), d: mealLabel(d.meals?.dinner),
        sd: d.sleepDuration || '', sh: d.sleepHours ?? '', nh: d.napHours ?? '', nw: d.nightWake ?? 0,
        ph: d.phoneBeforeSleep ? '是' : '', li: d.listenBeforeSleep ? '是' : '',
        bm: d.boneMass ?? '', wt: d.weight ?? '', ef: d.efficiency || '',
        em: (d.emotions || []).join('/'), ex: (d.exerciseTypes || []).join('/'), me: d.mainEvent || '',
      });
    });
    styleDataHeader(meta);

    // Sheet 2 · 时间块（合并相邻同分类，一块一行）
    const blocks = wb.addWorksheet('时间块');
    blocks.columns = [
      { header: '日期', key: 'date', width: 12 }, { header: '轨道', key: 'tr', width: 8 },
      { header: '开始', key: 's', width: 8 }, { header: '结束', key: 'e', width: 8 },
      { header: '分类', key: 'cat', width: 12 }, { header: '备注', key: 'note', width: 24 },
    ];
    dates.forEach(k => {
      dayBlocks(data[k]).forEach(run => {
        if (!run.catId) return;
        const c = CAT_BY_ID[run.catId];
        blocks.addRow({ date: k, tr: DATA_TRACK_NAMES[run.track] || '', s: run.start, e: run.end, cat: (c && (c.label || c.name)) || run.catId, note: run.note });
      });
    });
    styleDataHeader(blocks);

    // Sheet 3 · 行经记录（只列有经期/排卵阶段的天）
    const cyc = wb.addWorksheet('行经记录');
    cyc.columns = [
      { header: '日期', key: 'date', width: 12 }, { header: '阶段', key: 'phase', width: 8 },
      { header: '血量', key: 'flow', width: 8 }, { header: '疼痛', key: 'pain', width: 8 },
      { header: '症状', key: 'sym', width: 20 },
    ];
    dates.forEach(k => {
      const c = data[k] && data[k].cycle;
      if (!c || !c.phase || c.phase === 'none') return;
      cyc.addRow({
        date: k, phase: CYCLE_PHASE_LABEL[c.phase] || c.phase,
        flow: c.phase === 'menses' ? (CYCLE_FLOW_LABEL[c.flow] || '') : '',
        pain: (typeof c.pain === 'number') ? (CYCLE_PAIN_LABEL[c.pain] || '') : '',
        sym: (c.symptoms || []).join('/'),
      });
    });
    styleDataHeader(cyc);

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `时间块·数据_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 200);
  } catch (e) {
    console.error(e);
    alert('导出失败：' + e.message);
  }
}

// ---- Import ----
export async function parseTemplateFile(file) {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  const days = [];
  wb.eachSheet((ws) => {
    const name = ws.name;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(name)) return;
    let headerRowIdx = 1;
    for (let r = 1; r <= 5; r++) {
      const row = ws.getRow(r);
      let hits = 0;
      for (let c = 2; c <= 50; c++) {
        const v = row.getCell(c).value;
        if (v && /^\d{1,2}:\d{2}$/.test(String(v))) hits++;
        if (hits >= 3) break;
      }
      if (hits >= 3) { headerRowIdx = r; break; }
    }
    const headerRow = ws.getRow(headerRowIdx);
    const slotMap = [];
    for (let c = 2; c <= 200; c++) {
      const v = headerRow.getCell(c).value;
      if (v == null || v === '') continue;
      const m = String(v).match(/^(\d{1,2}):(\d{2})$/);
      if (m) slotMap.push({ col: c, h: +m[1], m: +m[2] });
    }
    if (slotMap.length === 0) return;
    const probeLabel = ws.getRow(headerRowIdx + 2).getCell(1).value;
    const isInterleaved = probeLabel && /^备注/.test(String(probeLabel));
    const trackRowIdx = isInterleaved
      ? [headerRowIdx + 1, headerRowIdx + 3, headerRowIdx + 5]
      : [headerRowIdx + 1, headerRowIdx + 2, headerRowIdx + 3];
    const noteRowIdx = isInterleaved
      ? [headerRowIdx + 2, headerRowIdx + 4, headerRowIdx + 6]
      : [headerRowIdx + 4, headerRowIdx + 5, headerRowIdx + 6];
    const tracks = [[], [], []];
    const notes = {};
    const meta = {};
    for (let t = 0; t < 3; t++) {
      const row = ws.getRow(trackRowIdx[t]);
      slotMap.forEach((s, sIdx) => {
        const cell = row.getCell(s.col);
        const fill = cell.fill;
        let rgb = null;
        if (fill && fill.type === 'pattern' && fill.fgColor && fill.fgColor.argb) {
          rgb = fill.fgColor.argb.slice(-6).toUpperCase();
        }
        const cat = rgb ? colorToCat(rgb) : null;
        tracks[t][sIdx] = cat || null;
      });
    }
    for (let t = 0; t < 3; t++) {
      const row = ws.getRow(noteRowIdx[t]);
      let runStart = null, runCat = null, runNoteWritten = false;
      slotMap.forEach((s, sIdx) => {
        const cat = tracks[t][sIdx];
        if (cat !== runCat) { runStart = sIdx; runCat = cat; runNoteWritten = false; }
        if (cat == null) return;
        const v = row.getCell(s.col).value;
        if (v != null && v !== '' && !runNoteWritten) {
          notes[`${t}:${runStart}`] = String(v);
          runNoteWritten = true;
        }
      });
    }
    const metaStartScan = headerRowIdx + 7;
    const collectedEmo = [];
    const collectedEx = [];
    for (let r = metaStartScan; r <= metaStartScan + 32; r++) {
      const labelCell = ws.getRow(r).getCell(1).value;
      if (!labelCell) continue;
      const label = String(labelCell).trim();
      const def = META_ROWS.find(m => m[0] === label);
      if (!def) continue;
      const [, key, kind, opt = {}] = def;
      const raw = ws.getRow(r).getCell(2).value;
      if (opt.multiSrc) {
        const parsed = cellValToMeta(raw, kind);
        if (parsed) {
          const optionName = key.split('.')[1];
          if (opt.multiSrc === 'emotions') collectedEmo.push(optionName);
          else if (opt.multiSrc === 'exerciseTypes') collectedEx.push(optionName);
        }
        continue;
      }
      const parsed = cellValToMeta(raw, kind);
      if (parsed != null) setDeep(meta, key, parsed);
    }
    if (collectedEmo.length) meta.emotions = collectedEmo;
    if (collectedEx.length) meta.exerciseTypes = collectedEx;
    days.push({ date: name, slotMap, tracks, notes, meta });
  });
  return days;
}

export async function importTemplate(existingData, setData) {
  try {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xlsm';
    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;
      try {
        const days = await parseTemplateFile(file);
        if (days.length === 0) { alert('未在该文件里找到任何日期 sheet（命名应为 YYYY-MM-DD）'); return; }
        openImportModal(days, existingData, setData);
      } catch (e) {
        console.error(e);
        alert('解析失败：' + e.message);
      }
    };
    input.click();
  } catch (e) {
    console.error(e);
    alert('加载 ExcelJS 失败：' + e.message);
  }
}

function openImportModal(parsedDays, existingData, setData) {
  const root = document.createElement('div');
  document.body.appendChild(root);
  const reactRoot = createRoot(root);

  function close() {
    reactRoot.unmount();
    document.body.removeChild(root);
  }
  reactRoot.render(<ImportModal parsedDays={parsedDays} existingData={existingData} onConfirm={(merged) => { setData(merged); close(); }} onCancel={close} />);
}

function ImportModal({ parsedDays, existingData, onConfirm, onCancel }) {
  const [selected, setSelected] = useState(() => {
    const s = {}; parsedDays.forEach(d => { s[d.date] = true; }); return s;
  });
  const [conflictMode, setConflictMode] = useState('overwrite');

  function doImport() {
    const next = { ...existingData };
    parsedDays.forEach(d => {
      if (!selected[d.date]) return;
      const existing = next[d.date];
      const base = existing && conflictMode !== 'overwrite' ? existing : emptyDay(d.date);
      const newTracks = base.tracks ? base.tracks.map(t => [...t]) : [[], [], []];
      for (let t = 0; t < 3; t++) {
        d.slotMap.forEach((s, sIdx) => {
          const realIdx = SLOTS.findIndex(x => x.h === s.h && x.m === s.m);
          if (realIdx < 0) return;
          const cat = d.tracks[t][sIdx];
          if (cat == null) {
            if (conflictMode === 'overwrite') newTracks[t][realIdx] = null;
            return;
          }
          if (conflictMode === 'skip' && newTracks[t][realIdx]) return;
          newTracks[t][realIdx] = cat;
        });
      }
      const newNotes = { ...(base.notes || {}) };
      Object.entries(d.notes).forEach(([k, v]) => {
        const [tStr, sIdxStr] = k.split(':');
        const t = +tStr, sIdx = +sIdxStr;
        const ref = d.slotMap[sIdx];
        if (!ref) return;
        const realIdx = SLOTS.findIndex(x => x.h === ref.h && x.m === ref.m);
        if (realIdx < 0) return;
        const realKey = `${t}:${realIdx}`;
        if (conflictMode === 'skip' && newNotes[realKey]) return;
        newNotes[realKey] = v;
      });
      next[d.date] = { ...base, tracks: newTracks, notes: newNotes, ...(d.meta || {}) };
    });
    onConfirm(next);
  }

  const allSelected = parsedDays.every(d => selected[d.date]);

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal import-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">导入时间块模板</div>
        <div className="modal-hint">已识别 {parsedDays.length} 个日期 sheet，勾选要导入的日期</div>

        <div className="import-toolbar">
          <button className="btn" onClick={() => setSelected(allSelected ? {} : Object.fromEntries(parsedDays.map(d => [d.date, true])))}>{allSelected ? '取消全选' : '全选'}</button>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>冲突处理：</span>
          {[['overwrite', '覆盖'], ['skip', '跳过'], ['merge', '智能合并']].map(([k, n]) => (
            <button key={k} className={`btn ${conflictMode === k ? 'primary' : ''}`} onClick={() => setConflictMode(k)}>{n}</button>
          ))}
        </div>

        <div className="import-list">
          {parsedDays.map(d => (
            <ImportRow
              key={d.date}
              day={d}
              hasExisting={!!existingData[d.date]}
              checked={!!selected[d.date]}
              onToggle={() => setSelected(s => ({ ...s, [d.date]: !s[d.date] }))}
            />
          ))}
        </div>

        <div className="modal-actions">
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={onCancel}>取消</button>
          <button className="btn primary" onClick={doImport}>确认导入 {Object.values(selected).filter(Boolean).length} 天</button>
        </div>
      </div>
    </div>
  );
}

function ImportRow({ day, hasExisting, checked, onToggle }) {
  const stripe = new Array(24).fill(null);
  for (let h = 0; h < 24; h++) {
    const slotsAtH = day.slotMap.map((s, i) => ({ s, i })).filter(x => x.s.h === h);
    const counts = {};
    slotsAtH.forEach(({ i }) => {
      for (let t = 0; t < 3; t++) {
        const c = day.tracks[t][i];
        if (c) counts[c] = (counts[c] || 0) + (t === 1 ? 2 : 1);
      }
    });
    let best = null, n = 0;
    for (const k in counts) if (counts[k] > n) { best = k; n = counts[k]; }
    stripe[h] = best;
  }
  return (
    <label className="import-row">
      <input type="checkbox" checked={checked} onChange={onToggle} />
      <span className="import-date">{day.date}</span>
      {hasExisting && <span className="import-flag" title="本地已有数据">⚠ 已有</span>}
      <span className="import-stripe">
        {stripe.map((c, h) => (
          <span key={h} className="import-stripe-cell" title={`${h}:00`} style={{ background: c ? `var(--c-${c === 'prep_xnx' ? 'prep1' : c === 'prep_pr' ? 'prep2' : c})` : 'transparent' }} />
        ))}
      </span>
    </label>
  );
}
